import { useState, useEffect } from 'react';
import {
    collectionGroup,
    query,
    where,
    getDocs,
    doc,
    getDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';

export const usePayouts = (options = {}) => {
    const {
        realtime = false,           // can be used later with onSnapshot
        status = null,
        typeTransfer = 'offline',   // we'll force this to 'offline' for now
    } = options;

    const [payouts, setPayouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchPayouts = async () => {
        setLoading(true);
        setError(null);

        try {
            // ─────────────────────────────────────────────
            //  Main query: collection group + type filter
            // ─────────────────────────────────────────────
            let q = query(
                collectionGroup(db, 'Payouts'),
                where('type', '==', typeTransfer)   // "offline"
            );

            // Optional extra filter on payout status (if provided)
            if (status) {
                q = query(q, where('status', '==', status));
            }

            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                setPayouts([]);
                setLoading(false);
                return [];
            }

            // ─────────────────────────────────────────────
            //  Enrich with artist data + filter only artists
            // ─────────────────────────────────────────────
            const enrichedPromises = snapshot.docs.map(async (payoutDoc) => {
                const payoutData = payoutDoc.data();
                const payoutId = payoutDoc.id;

                // Parent document = user
                const userRef = payoutDoc.ref.parent.parent;
                if (!userRef) return null;

                const userSnap = await getDoc(userRef);
                if (!userSnap.exists()) return null;

                const userData = userSnap.data();

                // Filter: only real artists
                const isArtist = 
                    userData.type === 'artist' || 
                    userData.role === 'artist';

                if (!isArtist) return null;

                // Build object — try to match fields your component expects
                return {
                    id: payoutId,
                    payoutId: payoutId,               // alias
                    ...payoutData,

                    // Artist info (used in your table)
                    artist_name: 
                        [userData.first_name, userData.last_name]
                            .filter(Boolean)
                            .join(' ')
                            .trim() || 'Unknown Artist',
                    artist_email: userData.email || 'N/A',
                    artist_uuid: userData.uid || 'N/A',
                    artist_profile_picture: userData.profile_picture || '',
                    recipientName: 
                        [userData.first_name, userData.last_name]
                            .filter(Boolean)
                            .join(' ')
                            .trim() || userData.username || 'Unknown',

                    // Common fallback / normalization fields
                    amount: payoutData.total_earnings 
                        || payoutData.amount 
                        || payoutData.totalTransfer 
                        || 0,
                    totalTransfer: payoutData.total_earnings || payoutData.amount || 0,
                    currency: payoutData.currency || 'usd',
                    status: payoutData.status || 'unknown',
                    created: payoutData.created 
                        || payoutData.createdAt 
                        || payoutData.processed_at 
                        || null,
                    createdAt: payoutData.created 
                        || payoutData.createdAt 
                        || payoutData.processed_at 
                        || null,

                    // Optional extra fields you might use later
                    payment_method: payoutData.payment_method || '—',
                    payment_info: payoutData.payment_info || '—',
                    note: payoutData.metadata?.note || '—',
                    reason:
                        payoutData.failure_reason
                        || payoutData.failure_message
                        || payoutData.reason
                        || payoutData.metadata?.reason
                        || payoutData.metadata?.failure_reason
                        || null,
                };
            });

            const results = (await Promise.all(enrichedPromises))
                .filter(Boolean)           // remove nulls
                .sort((a, b) => {          // optional: newest first
                    const da = a.created?.toMillis?.() ?? a.created?.seconds ?? 0;
                    const db = b.created?.toMillis?.() ?? b.created?.seconds ?? 0;
                    return db - da;
                });

            setPayouts(results);
            setLoading(false);
            return results;

        } catch (err) {
            console.error('Error fetching offline artist payouts:', err);
            setError(err.message || 'Failed to load offline artist payouts');
            setPayouts([]);
            setLoading(false);
            return [];
        }
    };

    useEffect(() => {
        fetchPayouts();
        // If you later want realtime → add onSnapshot logic here
    }, [status, typeTransfer]); // re-run when filters change

    return {
        payouts,
        loading,
        error,
        refetch: fetchPayouts,
    };
};

// You can keep getPayoutStats as-is — it should work with the enriched objects
export const getPayoutStats = (payouts) => {
    const totalPayouts = payouts.length;
    const paid = payouts.filter((p) => p.status?.toLowerCase() === 'paid').length;
    const pending = payouts.filter((p) => p.status?.toLowerCase() === 'pending').length;
    const failed = payouts.filter((p) => p.status?.toLowerCase() === 'failed').length;

    const totalAmount = payouts
        .reduce((sum, p) => {
            const amt = parseFloat(p.amount || p.totalTransfer || 0);
            return sum + (amt > 1000 ? amt / 100 : amt);
        }, 0)
        .toFixed(2);

    return { 
        totalPayouts, 
        paidPayouts: paid, 
        pendingPayouts: pending, 
        failedPayouts: failed, 
        totalAmount 
    };
};